import {
  BrowserRouter as Router,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Suspense, useMemo } from "react";
import { lazyRoute as lazy } from "./components/routing/lazyRoute";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { buildKardaMuiTheme } from "./theme/buildKardaMuiTheme";
import { queryClient } from "./shared/api/queryClient";
import { ToastProvider } from "./shared/toast";
import { ErrorBoundary } from "./components/errors/ErrorBoundary";
import { RouteErrorBoundary } from "./components/errors/RouteErrorBoundary";
import { RouteLoading } from "./components/routing/RouteLoading";

import LoginPage from "./pages/Login";
import NotFound404 from "./pages/NotFound";
import { ReviewPlanRouteGuard } from "./pages/ReviewPlan/ReviewPlanRouteGuard";
import { AuthTokenBridge } from "./components/auth/AuthTokenBridge";
import { AuthCallback } from "./pages/AuthCallback";
import { InitiateLogin } from "./pages/InitiateLogin";
import { RequirePermission } from "./components/auth/RequirePermission";
import { RequireCoApproval } from "./components/auth/RequireCoApproval";
import {
  USERS_ADMIN_PERMISSION,
  REPORTS_MANAGE_STATION_PERMISSION,
  EVIDENCE_EDIT_OWN_PERMISSION,
  EVIDENCE_EDIT_STATION_PERMISSION,
} from "./shared/auth/authorization";
import { ColorModeProvider, useColorMode } from "./context/ColorModeContext";
import { OrgIdProvider } from "./shared/auth/orgContext";
import { RequireRole } from "./components/auth/RequireRole";
import { STATION_ADMIN_ROLE } from "./shared/auth/authorization";
import { RequireStationAdminArea } from "./components/auth/RequireStationAdminArea";
import { RequireInternalAffairs } from "./components/auth/RequireInternalAffairs";

// Public auth routes stay immediately available. Protected features load only
// when their guarded route renders; direct module imports keep page barrels
// from pulling unrelated editors or admin sections into the same entry chunk.
const ProtectedView = lazy(() => import("./components/layout/ProtectedView").then(
  (module) => ({ default: module.ProtectedView }),
));
const HomePage = lazy(() => import("./pages/Home/HomePage"));
const ProfilePage = lazy(() => import("./pages/Profile/ProfilePage"));
const ReleaseNotesPage = lazy(() => import("./pages/ReleaseNotes/ReleaseNotesPage"));
const VideoSelectionPage = lazy(() => import("./pages/VideoSelection/VideoSelectionPage"));
const TranscriptsListPage = lazy(() => import("./pages/Transcripts/TranscriptsListPage"));
const TranscriptViewPage = lazy(() => import("./pages/Transcripts/TranscriptViewPage"));
const GenerateReportPage = lazy(() => import("./pages/GenerateReport/GenerateReportPage"));
const GeneratingPage = lazy(() => import("./pages/Generating/GeneratingPage"));
const EditSectionPage = lazy(() => import("./pages/EditSection/EditSectionPage"));
const ConfirmationPage = lazy(() => import("./pages/Confirmation/ConfirmationPage"));
const ReviewPlanPage = lazy(() => import("./pages/ReviewPlan/ReviewPlanPage"));
const ExtractedInfoPage = lazy(() => import("./pages/ExtractedInfo/ExtractedInfoPage").then(
  (module) => ({ default: module.ExtractedInfoPage }),
));
const FinalEditsPage = lazy(() => import("./pages/FinalEdits/FinalEditsPage").then(
  (module) => ({ default: module.FinalEditsPage }),
));
const ManageUsersPage = lazy(() => import("./pages/Admin/ManageUsers/ManageUsersPage"));
const ConnectBodycam = lazy(() => import("./pages/ConnectBodycam/ConnectBodycam").then(
  (module) => ({ default: module.ConnectBodycam }),
));
const AnalyticsPage = lazy(() => import("./pages/Analytics/AnalyticsPage").then(
  (module) => ({ default: module.AnalyticsPage }),
));
const CoReviewQueuePage = lazy(() => import("./pages/CoReview/CoReviewQueuePage"));
const CoReviewPage = lazy(() => import("./pages/CoReview/CoReviewPage"));
const KardaAdminProvider = lazy(() => import("./pages/KardaAdmin/KardaAdminContext").then(
  (module) => ({ default: module.KardaAdminProvider }),
));
const KardaAdminLayout = lazy(() => import("./pages/KardaAdmin/KardaAdminLayout"));
const KardaAdminPage = lazy(() => import("./pages/KardaAdmin/KardaAdminPage"));
const KardaStationDetailPage = lazy(() => import("./pages/KardaAdmin/KardaStationDetailPage"));
const StationDetailsSection = lazy(() => import("./pages/KardaAdmin/StationDetailsSection"));
const StationUsersSection = lazy(() => import("./pages/KardaAdmin/StationUsersSection"));
const StationBodycamSection = lazy(() => import("./pages/KardaAdmin/StationBodycamSection"));
const StationSettingsSection = lazy(() => import("./pages/KardaAdmin/StationSettingsSection"));
const UserMappingsSection = lazy(() => import("./pages/KardaAdmin/UserMappingsSection"));
const TemplateUploadPage = lazy(() => import("./pages/TemplateUpload/TemplateUploadPage"));
const StationAdminLayout = lazy(() => import("./pages/StationAdmin/StationAdminLayout"));
const StationAdminIndex = lazy(() => import("./pages/StationAdmin/StationAdminIndex"));
const StationAdminInternalAffairs = lazy(() => import("./pages/StationAdmin/StationAdminInternalAffairs"));

function AppContent() {
  const { mode } = useColorMode();
  const theme = useMemo(() => buildKardaMuiTheme(mode), [mode]);

  const protectedPages = (
    <>
      <Route path="/" element={<HomePage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/release-notes" element={<ReleaseNotesPage />} />
      <Route path="/video-selection" element={<VideoSelectionPage />} />
      <Route
        path="/transcripts"
        element={
          <RequirePermission
            permission={[
              EVIDENCE_EDIT_OWN_PERMISSION,
              EVIDENCE_EDIT_STATION_PERMISSION,
            ]}
          >
            <TranscriptsListPage />
          </RequirePermission>
        }
      />
      <Route
        path="/transcripts/:evidenceId"
        element={
          <RequirePermission
            permission={[
              EVIDENCE_EDIT_OWN_PERMISSION,
              EVIDENCE_EDIT_STATION_PERMISSION,
            ]}
          >
            <TranscriptViewPage />
          </RequirePermission>
        }
      />
      <Route
        path="/generate-report/:evidenceID"
        element={<GenerateReportPage />}
      />
      <Route path="/generating/:jobId" element={<GeneratingPage />} />
      <Route
        path="/preview-report-info/:reportID"
        element={<ExtractedInfoPage />}
      />
      <Route path="/edit-section/:reportID" element={<EditSectionPage />} />
      <Route
        path="/review-plan/:reportID?"
        element={
          <ReviewPlanRouteGuard>
            <ReviewPlanPage />
          </ReviewPlanRouteGuard>
        }
      />
      <Route path="/final-edits/:reportID" element={<FinalEditsPage />} />
      {/* Param optional: pre-rename bookmarks of the bare /confirmation sign
          page get the page's clean home redirect instead of the 404. */}
      <Route
        path="/confirmation/:reportID?"
        element={<ConfirmationPage />}
      />
      <Route
        path="/co-review"
        element={
          <RequireCoApproval>
            <RequirePermission permission={REPORTS_MANAGE_STATION_PERMISSION}>
              <CoReviewQueuePage />
            </RequirePermission>
          </RequireCoApproval>
        }
      />
      <Route
        path="/co-review/:reportID"
        element={
          <RequireCoApproval>
            <RequirePermission permission={REPORTS_MANAGE_STATION_PERMISSION}>
              <CoReviewPage />
            </RequirePermission>
          </RequireCoApproval>
        }
      />
      <Route
        path="/manage-users"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ManageUsersPage />
          </RequirePermission>
        }
      />
      <Route
        path="/karda-admin"
        element={
          <RequireRole roleSlug="org-karda-admin">
            <KardaAdminProvider />
          </RequireRole>
        }
      >
        <Route element={<KardaAdminLayout />}>
          <Route index element={<KardaAdminPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="templates" element={<TemplateUploadPage />} />
          <Route
            path="stations/:stationId"
            element={<KardaStationDetailPage />}
          >
            <Route index element={<StationDetailsSection />} />
            <Route path="users" element={<StationUsersSection />} />
            <Route path="bodycam" element={<StationBodycamSection />} />
            <Route path="mappings" element={<UserMappingsSection />} />
            <Route path="settings" element={<StationSettingsSection />} />
          </Route>
        </Route>
      </Route>
      {/* Station Admin is reachable when EITHER per-station flag is on —
          `admin-dashboard` (Overview) or `internal-affairs` (IA tab) — via
          RequireStationAdminArea (404 only when both are off), then gated by
          the station_admin role. Each destination keeps its own flag: the
          index resolves to the Overview dashboard when `admin-dashboard` is on
          and otherwise redirects to the IA tab; the IA route is wrapped in
          RequireInternalAffairs. Flag guard outside role guard so a fully
          disabled station sees "this feature does not exist" regardless of
          role. */}
      <Route
        path="/station-admin"
        element={
          <RequireStationAdminArea>
            <RequireRole roleSlug={STATION_ADMIN_ROLE}>
              <StationAdminLayout />
            </RequireRole>
          </RequireStationAdminArea>
        }
      >
        <Route index element={<StationAdminIndex />} />
        {/* Internal Affairs is gated a SECOND time, by its own per-station
            `internal-affairs` flag (RequireInternalAffairs), so a station can
            run the dashboard with the IA tab on or off independently of
            `admin-dashboard`. The nav tab hides itself the same way. */}
        <Route
          path="internal-affairs"
          element={
            <RequireInternalAffairs>
              <StationAdminInternalAffairs />
            </RequireInternalAffairs>
          }
        />
      </Route>
      <Route
        path="/connect-bodycam"
        element={
          <RequirePermission permission={USERS_ADMIN_PERMISSION}>
            <ConnectBodycam />
          </RequirePermission>
        }
      />
    </>
  );

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
      <Router useTransitions={false}>
        <AuthTokenBridge />
        <Routes>
          <Route
            path="/login"
            element={
              <RouteErrorBoundary>
                <LoginPage />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/initiate-login"
            element={
              <RouteErrorBoundary>
                <InitiateLogin />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/callback"
            element={
              <RouteErrorBoundary>
                <AuthCallback />
              </RouteErrorBoundary>
            }
          />
          <Route
            path="/"
            element={
              <RouteErrorBoundary>
                <Suspense fallback={<RouteLoading />}>
                  <ProtectedView>
                    <RouteErrorBoundary>
                      {/* Keep the shell mounted and show immediate feedback
                          while a destination chunk loads. */}
                      <Suspense fallback={<RouteLoading />}>
                        <Outlet />
                      </Suspense>
                    </RouteErrorBoundary>
                  </ProtectedView>
                </Suspense>
              </RouteErrorBoundary>
            }
          >
            {protectedPages}
          </Route>
          <Route
            path="*"
            element={
              <RouteErrorBoundary>
                <NotFound404 />
              </RouteErrorBoundary>
            }
          />
        </Routes>
      </Router>
      </ToastProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary variant="bare">
      {/* Above ColorModeProvider/OrgIdProvider: the query layer has no
          theme or auth dependency, and the module-singleton client must sit
          over everything that will query. */}
      <QueryClientProvider client={queryClient}>
        <ColorModeProvider>
          <OrgIdProvider>
            <AppContent />
          </OrgIdProvider>
        </ColorModeProvider>
        {/* Compiles to a no-op in production builds. */}
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
